import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import styled, { keyframes, css } from 'styled-components';
import PropTypes from 'prop-types';
import DialogHeader from '../DialogHeader';
import Container from '../Container';
import Text from '../Text';
import EmptyButton from '../EmptyButton';
import ClickOutside from '../ClickOutside';
import CloseIcon from '../../images/CloseIcon';

const slideBottomIn = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideBottomOut = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const slideTopIn = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideTopOut = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100%);
  }
`;

const slideLeftIn = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideLeftOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const slideRightIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideRightOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const animationTypes = {
  slideBottom: {
    in: slideBottomIn,
    out: slideBottomOut,
  },
  slideTop: {
    in: slideTopIn,
    out: slideTopOut,
  },
  slideLeft: {
    in: slideLeftIn,
    out: slideLeftOut,
  },
  slideRight: {
    in: slideRightIn,
    out: slideRightOut,
  },
};

const fadeAnimation = ({ show, animationDuration }) =>
  css`
    ${show ? fadeIn : fadeOut} ${animationDuration}ms
  `;

const slideAnimation = ({ show, animationDuration, animationType }) =>
  css`
    ${show
      ? animationTypes[animationType].in
      : animationTypes[animationType].out} ${animationDuration}ms
  `;

const FadeContainer = styled(Container)`
  display: flex;
  opacity: ${props => (props.show ? 1 : 0)};
  animation: ${fadeAnimation};
`;

const SlideContainer = styled(Container)`
  display: flex;
  transform: translateY(${props => (props.show ? 0 : '100%')};);
  animation: ${slideAnimation};
`;

const defaultRender = data => {
  const { header, children, onClose } = data;
  return (
    <>
      {typeof header === 'string' ? (
        <DialogHeader>
          <Text
            width="120px"
            flex="1 0 auto"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {header}
          </Text>
          <EmptyButton
            onClick={onClose}
            display="flex"
            alignItems="center"
            overflow="hidden"
          >
            <CloseIcon />
          </EmptyButton>
        </DialogHeader>
      ) : (
        header
      )}
      <Container flex="0 1 100%">{children}</Container>
    </>
  );
};

const Dialog = ({
  show,
  render,
  children,
  header,
  onClose,
  onReady,
  mode,
  animationDuration,
  animationType,
  clickOutside,
  zIndex,
  maxWidth,
  allowTouchMoveQuerySelectors,
  fadeContainerProps,
  ...props
}) => {
  const [inDom, setInDom] = useState(false);
  const [containerRef, setContainerRef] = useState();
  const [containerOverflow, setContainerOverflow] = useState('visible');
  const resizeObserverRef = useRef();

  useEffect(() => {
    if (show) {
      setInDom(true);
    } else {
      setTimeout(() => {
        setInDom(false);
      }, animationDuration);
    }
    return () => {
      if (containerRef) {
        enableBodyScroll(containerRef);
      }
      if (resizeObserverRef.current && containerRef) {
        resizeObserverRef.current.unobserve(containerRef);
      }
    };
  }, [show, animationDuration, containerRef]);

  const allowTouchMove = useCallback(
    target =>
      allowTouchMoveQuerySelectors.some(selector => {
        const elm = document.querySelector(selector);
        return elm ? elm.contains(target) : false;
      }),
    [allowTouchMoveQuerySelectors]
  );

  useEffect(() => {
    if (inDom && containerRef) {
      onReady();
      disableBodyScroll(containerRef, {
        allowTouchMove,
      });
    }
    if (!inDom && containerRef) {
      enableBodyScroll(containerRef);
    }
  }, [containerRef, inDom, onReady, allowTouchMove]);

  const setOverflow = useCallback(() => {
    if (!containerRef) {
      return;
    }

    const { innerHeight } = window;
    const { offsetHeight } = containerRef || {};

    if (innerHeight > offsetHeight) {
      setContainerOverflow('visible');
    } else {
      setContainerOverflow('auto');
    }
  }, [containerRef]);

  useEffect(() => {
    if (containerRef) {
      const resizeObserver = new ResizeObserver(setOverflow);
      resizeObserver.observe(containerRef);
      resizeObserverRef.current = resizeObserver;
    }
  }, [containerRef, setOverflow]);

  const dialogStyle = useMemo(
    () =>
      mode !== 'desktop'
        ? {
            position: 'fixed',
            top: '0px',
            left: '0px',
            right: '0px',
            bottom: '0px',
            borderRadius: '0',
          }
        : {},
    [mode]
  );

  if (!inDom) return null;

  return createPortal(
    <FadeContainer
      position="fixed"
      zIndex={zIndex}
      top="0"
      left="0"
      right="0"
      bottom="0"
      backgroundColor="rgba(0, 0, 0, 0.4)"
      show={show}
      animationDuration={animationDuration}
      {...fadeContainerProps}
    >
      <ClickOutside
        margin="auto"
        display="flex"
        maxHeight="100%"
        onClickOutside={clickOutside ? onClose : () => {}}
      >
        <SlideContainer
          ref={ref => {
            setContainerRef(ref);
          }}
          animationType={animationType}
          display="flex"
          flexDirection="column"
          border="solid 1px"
          borderColor="border300"
          backgroundColor="background100"
          boxShadow="0 0 20px 0 rgba(0, 0, 0, 0.2)"
          width="100vw"
          show={show}
          mode={mode}
          animationDuration={animationDuration}
          overflow={containerOverflow}
          borderRadius="11px"
          maxHeight="100%"
          maxWidth={mode === 'desktop' ? maxWidth : 'unset'}
          {...dialogStyle}
          {...props}
        >
          {render({
            header,
            children,
            onClose,
          })}
        </SlideContainer>
      </ClickOutside>
    </FadeContainer>,
    document.body
  );
};

Dialog.propTypes = {
  show: PropTypes.bool,
  render: PropTypes.func,
  children: PropTypes.node,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClose: PropTypes.func,
  onReady: PropTypes.func,
  mode: PropTypes.oneOf(['desktop', 'mobile']),
  maxWidth: PropTypes.string,
  animationDuration: PropTypes.number,
  animationType: PropTypes.oneOf([
    'slideBottom',
    'slideTop',
    'slideLeft',
    'slideRight',
  ]),
  clickOutside: PropTypes.bool,
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allowTouchMoveQuerySelectors: PropTypes.arrayOf(PropTypes.string),
  fadeContainerProps: PropTypes.shape({}),
};

Dialog.defaultProps = {
  show: false,
  render: defaultRender,
  children: null,
  header: null,
  onClose: () => {},
  onReady: () => {},
  mode: 'desktop',
  maxWidth: '600px',
  animationDuration: 300,
  animationType: 'slideBottom',
  clickOutside: false,
  zIndex: '99',
  allowTouchMoveQuerySelectors: [],
  fadeContainerProps: {},
};

export default Dialog;
